import React, { useEffect, useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import dayjs from "@/lib/helpers/dayjs";
import { Modal2 } from "@/ui2/molecules/Modal";
import { useGenerateOtc } from "@/features/patient/api/mutations/useGenerateOtc";

type Props = {
  patientId: string;
  trigger: React.ReactElement;
};

export function OtcModal({ patientId, trigger }: Props) {
  const [otc, setOtc] = useState<string | null>(null);
  const [otcExpiration, setOtcExpiration] = useState<string | null>(null);
  const [expirationTimer, setExpirationTimer] = useState("0:00");
  const { mutateAsync: generateOtc } = useGenerateOtc();

  const isExpired = otcExpiration !== null && dayjs.utc().isAfter(dayjs.utc(otcExpiration));

  useEffect(() => {
    if (otcExpiration === null || isExpired) return undefined;

    setExpirationTimer(computeTimer(otcExpiration));

    const id = setInterval(() => {
      setExpirationTimer(computeTimer(otcExpiration));
    }, 1000);

    return () => clearInterval(id);
  }, [otcExpiration, isExpired]);

  const handleGenerateOTC = async () => {
    try {
      if (otcExpiration === null || dayjs.utc().isAfter(dayjs.utc(otcExpiration))) {
        const response = await generateOtc({ patientId });

        setOtc(response.code);
        setOtcExpiration(response.expiration);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Modal2
      className="tw:max-w-60"
      overlayProps={{
        onOpenChange: (isOpen) => {
          if (isOpen) handleGenerateOTC();
        },
      }}
      title="One-time code"
      trigger={trigger}
    >
      <div className="tw:text-center">
        {!isExpired && (
          <>
            <p className="tw:my-3">
              The code below helps a patient sign in to their aptihealth account from a patient
              tablet. Securely provide the 5 character code to the patient, and assist them in
              entering the code on the tablet.
            </p>

            <div className="my-4 fs-32 tw:font-bold" style={{ letterSpacing: 5 }}>
              {otc}
            </div>

            <p className="tw:my-3">
              The one-time code expires in{" "}
              <span className="tw:font-bold">{expirationTimer} minutes</span>
            </p>
          </>
        )}

        {isExpired && (
          <>
            <div className="my-5 txt-error">
              <div>One-time code has expired.</div>
              <div>To generate a new code, click the button below.</div>
            </div>

            <div className="my-5">
              <Button2 onPress={handleGenerateOTC} variant="primary">
                Generate new code
              </Button2>
            </div>
          </>
        )}
      </div>
    </Modal2>
  );
}

function computeTimer(expiration: string): string {
  const totalSeconds = dayjs.utc(expiration).diff(dayjs.utc(), "second");
  const mins = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return mins >= 0 && seconds >= 0 ? `${mins}:${seconds < 10 ? "0" + seconds : seconds}` : "0:00";
}
